import * as React from "react"
import * as ReactDOM from "react-dom"

export interface Action<a> extends Fun<a,a> {}
export interface Unit {}
export type Fun<a,b> = (_:a) => b
export let then = <a,b,c>(f:Fun<a,b>, g:Fun<b,c>) : Fun<a,c> => (x:a) => g(f(x))
export interface Pair<a,b> { fst:a, snd:b }
export let mk_pair = <a,b>(a:a, b:b) => ({ fst:a, snd:b })
export interface Sum<a,b> { v: { kind:"l", v:a } | { kind:"r", v:b } }
export let inl = <a,b>(a:a) : Sum<a,b> => ({ v: { kind:"l", v:a } })
export let inr = <a,b>(b:b) : Sum<a,b> => ({ v: { kind:"r", v:b } })
export interface Option<a> extends Sum<Unit,a> {}
export let none = function<a>() : Option<a> { return inl<Unit,a>({}) }
export let some = function<a>(v:a) : Option<a> { return inr<Unit,a>(v) }
export interface Options {
  readonly?:boolean
  autofocus?:boolean
  key?:string
  className?:string
  role?:string
  style?:React.CSSProperties
  id?:string
  extraProperties?:{}
}
export let option = function<t, k extends keyof t>(k:k, o?:t) : t[k] | undefined {
  return o ? o[k] : undefined
}

export interface WidgetBase<o> { run:(on_done:(output:o) => void) => JSX.Element }
export interface Widget<o> extends WidgetBase<o> {
  wrapHTML:(f:Fun<JSX.Element, JSX.Element>) => Widget<o>,
  map:<o2>(f:Fun<o,o2>) => Widget<o2>,
  filter:(p:Fun<o,boolean>) => Widget<o>,
  then:<o2>(k:Fun<o,Widget<o2>>) => Widget<o2>,
  never:<o2>() => Widget<o2>,
}

export let mkWidget = function<o>(w:WidgetBase<o>) : Widget<o> {
  return mk_widget(w)
}

export let jsxToWidget = function<o>(jsx:JSX.Element) : Widget<o> {
  return mkWidget({run: _ => jsx})
}

export let mk_widget = function<w extends WidgetBase<o>,o>(w:w) : Widget<o> {
  return {
    run:w.run,
    wrapHTML:function(f:Fun<JSX.Element,JSX.Element>) { return wrapHTML<o>(f)(this) },
    map:function<o2>(f:Fun<o,o2>) { return map(f)(this) },
    filter:function(p:Fun<o,boolean>) { return filter(p)(this) },
    then:function<o2>(k:Fun<o,Widget<o2>>) { return bind(this, k) },
    never:function<o2>() { return never(this) }
  }
}
export type IOWidget<i,o> = (input:i) => Widget<o>

export const nothing = function<o>() : Widget<o> {
  return mk_widget({
    run:(on_done_transform:(output:o) => void) : JSX.Element => {
      return null!
    }
  }) }

export let never_io = function<i,o1,o2>(inner_widget:IOWidget<i,o1>) :
  IOWidget<i,o2> {
  return (input:i) => mk_widget({
    run:(on_done_transform:(output:o2) => void) =>
      inner_widget(input).run(_ => {})
  })
}

export let never = function<o>(inner_widget:Widget<{}>) : Widget<o> {
  return never_io<Unit,{},o>(_ => inner_widget)({})
}

export let wrapHTML = function<a>(wrap:Fun<JSX.Element, JSX.Element>) : Fun<Widget<a>, Widget<a>> {
  return widget_a => mk_widget({
    ...widget_a,
    run:(on_done:(output:a) => void) =>
      wrap(widget_a.run(res_a => on_done(res_a))) }) }

export let map = function<a,b>(f:Fun<a,b>) : Fun<Widget<a>, Widget<b>> {
  return widget_a => mk_widget({
    ...widget_a,
    run:(on_done_b:(output_b:b) => void) =>
      widget_a.run(res_a => on_done_b(f(res_a))) }) }

export interface JoinerProps<a> {
  widget_a2:Widget<Widget<a>>
  on_done_a:(output_a:a) => void
}
export interface JoinerState<a> {
  inner_widget:Widget<a> | undefined
}
export class Joiner<a> extends React.Component<JoinerProps<a>, JoinerState<a>> {
  constructor(props:JoinerProps<a>, context:any) {
    super(props, context)

    this.state = { inner_widget:undefined }
  }

  render() {
    return <>
      {this.props.widget_a2.run(widget_a => this.setState(s => ({...s, inner_widget:widget_a})))}
      {this.state.inner_widget ?
        this.state.inner_widget.run(this.props.on_done_a)
      : null}
    </>
  }
}

export let join = function<a>(widget_a2:Widget<Widget<a>>) : Widget<a> {
  return mk_widget({
    run:(on_done_a:(output_a:a) => void) =>
      React.createElement<JoinerProps<a>>(Joiner, {widget_a2, on_done_a}) })
}

export let bind = function<a,b>(widget_a:Widget<a>, widget_b_from_a:Fun<a,Widget<b>>) : Widget<b> {
  return join(map(widget_b_from_a)(widget_a))
}

export let filter = function<a>(p:Fun<a,boolean>) : Fun<Widget<a>, Widget<a>> {
  return inner_widget => ({
    ...inner_widget,
    run:(on_done:(output:a) => void) =>
      inner_widget.run(res_a => p(res_a) ? on_done(res_a) : null) }) }


